'use client'

import { useState, useTransition } from 'react';
import themeStyles from './theme.module.css';

const IMAGE_SETTINGS = {
  maxWidth: 1800,
  maxHeight: 2400,
  quality: 0.82,
};

const INITIAL_FORM = {
  fullName: '',
  email: '',
  phone: '',
  city: '',
  facebookProfile: '',
  birthDay: '',
  birthMonth: '',
  birthYear: '',
  privacyConsent: false,
};

const DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => String(index + 1));
const MONTH_OPTIONS = [
  { value: '01', label: 'Ianuarie' },
  { value: '02', label: 'Februarie' },
  { value: '03', label: 'Martie' },
  { value: '04', label: 'Aprilie' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Iunie' },
  { value: '07', label: 'Iulie' },
  { value: '08', label: 'August' },
  { value: '09', label: 'Septembrie' },
  { value: '10', label: 'Octombrie' },
  { value: '11', label: 'Noiembrie' },
  { value: '12', label: 'Decembrie' },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 86 }, (_, index) => String(CURRENT_YEAR - 14 - index));

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Imaginea selectata nu a putut fi procesata.'));
    };

    image.src = objectUrl;
  });
}

function getResizedDimensions(width, height) {
  const ratio = Math.min(
    IMAGE_SETTINGS.maxWidth / width,
    IMAGE_SETTINGS.maxHeight / height,
    1
  );

  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

async function optimizeImageFile(file, index) {
  if (!file.type.startsWith('image/')) {
    throw new Error('Ambele fisiere trebuie sa fie imagini valide.');
  }

  const image = await loadImageElement(file);
  const { width, height } = getResizedDimensions(image.naturalWidth, image.naturalHeight);
  const canvas = document.createElement('canvas');

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Conversia imaginilor nu este disponibila in acest browser.');
  }

  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (!result) {
          reject(new Error('Conversia imaginilor a esuat.'));
          return;
        }

        resolve(result);
      },
      'image/webp',
      IMAGE_SETTINGS.quality
    );
  });

  const baseName = file.name.replace(/\.[^.]+$/, '') || `inscriere-${index + 1}`;

  return new File([blob], `${baseName}.webp`, {
    type: 'image/webp',
    lastModified: Date.now(),
  });
}

function buildBirthDate({ birthDay, birthMonth, birthYear }) {
  const day = String(birthDay || '').padStart(2, '0');
  const month = String(birthMonth || '').padStart(2, '0');
  const year = String(birthYear || '').trim();

  if (!day || !month || !year) {
    throw new Error('Completeaza ziua, luna si anul nasterii.');
  }

  return `${year}-${month}-${day}`;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function isValidOptionalUrl(value) {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return true;
  }

  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function validateBirthDateFields(form) {
  const day = Number.parseInt(String(form.birthDay || ''), 10);
  const month = Number.parseInt(String(form.birthMonth || ''), 10);
  const year = Number.parseInt(String(form.birthYear || ''), 10);

  if (!day || !month || !year) {
    return 'Completeaza ziua, luna si anul nasterii.';
  }

  const birthDate = new Date(Date.UTC(year, month - 1, day));
  const isSameDate =
    birthDate.getUTCFullYear() === year &&
    birthDate.getUTCMonth() === month - 1 &&
    birthDate.getUTCDate() === day;

  if (!isSameDate) {
    return 'Data nasterii nu este valida.';
  }

  const today = new Date();
  let age = today.getUTCFullYear() - birthDate.getUTCFullYear();
  const monthDifference = today.getUTCMonth() - birthDate.getUTCMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getUTCDate() < birthDate.getUTCDate())
  ) {
    age -= 1;
  }

  if (age < 14 || age > 99) {
    return 'Varsta trebuie sa fie intre 14 si 99 de ani.';
  }

  return '';
}

function validateForm(form, selectedFiles) {
  const errors = {};

  if (!String(form.fullName || '').trim()) {
    errors.fullName = 'Numele si prenumele sunt obligatorii.';
  }

  if (!String(form.email || '').trim()) {
    errors.email = 'Emailul este obligatoriu.';
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Adresa de email nu este valida.';
  }

  if (!String(form.phone || '').trim()) {
    errors.phone = 'Telefonul este obligatoriu.';
  }

  if (!String(form.city || '').trim()) {
    errors.city = 'Orasul este obligatoriu.';
  }

  if (!isValidOptionalUrl(form.facebookProfile)) {
    errors.facebookProfile = 'Profilul Facebook trebuie sa fie un link valid.';
  }

  const birthDateError = validateBirthDateFields(form);

  if (birthDateError) {
    errors.birthDate = birthDateError;
  }

  if (selectedFiles.length === 0) {
    errors.images = 'Te rugam sa selectezi cel putin o imagine.';
  } else if (!selectedFiles.every((file) => file.type.startsWith('image/'))) {
    errors.images = 'Toate fisierele trebuie sa fie imagini valide.';
  }

  if (!form.privacyConsent) {
    errors.privacyConsent = 'Este necesar acordul pentru prelucrarea datelor personale.';
  }

  return errors;
}

export default function InscriereForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSaving, startSaving] = useTransition();

  function resetFormState() {
    setForm(INITIAL_FORM);
    setSelectedFiles([]);
    setFieldErrors({});
    setError('');
    setSubmitted(false);
  }

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFieldErrors((current) => {
      if (!current[field] && !(field === 'birthDay' || field === 'birthMonth' || field === 'birthYear')) {
        return current;
      }

      const nextErrors = { ...current };

      if (field === 'birthDay' || field === 'birthMonth' || field === 'birthYear') {
        delete nextErrors.birthDate;
      } else {
        delete nextErrors[field];
      }

      return nextErrors;
    });
  }

  function handleFileChange(event) {
    const files = Array.from(event.target.files || []);
    setError('');
    setSelectedFiles(files);
    setFieldErrors((current) => {
      const nextErrors = { ...current };
      delete nextErrors.images;
      return nextErrors;
    });
  }

  function handleSubmit(event) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setError('');

    startSaving(async () => {
      try {
        const validationErrors = validateForm(form, selectedFiles);

        if (Object.keys(validationErrors).length > 0) {
          setFieldErrors(validationErrors);
          setError('Corecteaza campurile marcate mai jos.');
          return;
        }

        setFieldErrors({});
        const optimizedFiles = await Promise.all(
          selectedFiles.map((file, index) => optimizeImageFile(file, index))
        );

        const body = new FormData();
        body.append('fullName', form.fullName);
        body.append('email', form.email);
        body.append('phone', form.phone);
        body.append('city', form.city);
        body.append('facebookProfile', form.facebookProfile);
        body.append('birthDate', buildBirthDate(form));
        body.append('privacyConsent', form.privacyConsent ? 'true' : 'false');
        optimizedFiles.forEach((file) => {
          body.append('images', file);
        });

        const response = await fetch('/api/inscriere', {
          method: 'POST',
          body,
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          setError(payload.error || 'Inscrierea nu a putut fi trimisa.');
          return;
        }

        formElement.reset();
        setForm(INITIAL_FORM);
        setSelectedFiles([]);
        setFieldErrors({});
        setError('');
        setSubmitted(true);
      } catch (submitError) {
        setError(submitError.message || 'Inscrierea nu a putut fi trimisa.');
      }
    });
  }

  if (submitted) {
    return (
      <div className="card border-0 shadow rounded-4 overflow-hidden">
        <div className="card-body p-4 p-lg-5">
          <div className={themeStyles.successPanel}>
            <div className={themeStyles.successPanelGlow} aria-hidden="true" />
            <div className={themeStyles.successPanelBody}>
              <span className={themeStyles.successPanelEyebrow}>Inscriere trimisa</span>
              <h4 className={`${themeStyles.successPanelTitle} title`}>
                Multumim! Cererea ta a fost inregistrata.
              </h4>
              <p className={themeStyles.successPanelText}>
                Inscrierea a fost trimisa cu succes, iar echipa Luna Di Miele va analiza
                datele si fotografiile primite. Vei fi contactata de agentie pentru urmatorii pasi.
              </p>

              <div className={themeStyles.successPanelMeta}>
                <span className="badge text-bg-light border rounded-pill px-3 py-2">
                  Confirmare primita
                </span>
                <span className="text-muted">
                  Poti reveni oricand pentru o noua inscriere.
                </span>
              </div>

              <button
                type="button"
                className="btn btn-primary px-4 py-3 rounded-3 fw-semibold"
                onClick={resetFormState}
              >
                Trimite o noua inscriere
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow rounded-4 overflow-hidden">
      <div className="card-body p-4 p-lg-5">
        <div className="section-title mb-4">
          <h6 className="text-primary">Inscriere online</h6>
          <h4 className="title mb-3">Completeaza formularul de participare</h4>
          <p className="text-muted mb-0">
            Completeaza datele de mai jos si ataseaza fotografii recente pentru evaluare.
          </p>
        </div>

        {error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <div className="row g-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold" htmlFor="signup-full-name">
                Nume si prenume <span className="text-danger">*</span>
              </label>
              <input
                id="signup-full-name"
                type="text"
                className={`form-control ${fieldErrors.fullName ? 'is-invalid' : ''}`}
                value={form.fullName}
                onChange={(event) => updateField('fullName', event.target.value)}
              />
              {fieldErrors.fullName ? (
                <div className="invalid-feedback d-block">{fieldErrors.fullName}</div>
              ) : null}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold" htmlFor="signup-email">
                Email <span className="text-danger">*</span>
              </label>
              <input
                id="signup-email"
                type="email"
                className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                value={form.email}
                onChange={(event) => updateField('email', event.target.value)}
              />
              {fieldErrors.email ? (
                <div className="invalid-feedback d-block">{fieldErrors.email}</div>
              ) : null}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold" htmlFor="signup-phone">
                Telefon <span className="text-danger">*</span>
              </label>
              <input
                id="signup-phone"
                type="tel"
                className={`form-control ${fieldErrors.phone ? 'is-invalid' : ''}`}
                value={form.phone}
                onChange={(event) => updateField('phone', event.target.value)}
              />
              {fieldErrors.phone ? (
                <div className="invalid-feedback d-block">{fieldErrors.phone}</div>
              ) : null}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold" htmlFor="signup-city">
                Orasul <span className="text-danger">*</span>
              </label>
              <input
                id="signup-city"
                type="text"
                className={`form-control ${fieldErrors.city ? 'is-invalid' : ''}`}
                value={form.city}
                onChange={(event) => updateField('city', event.target.value)}
              />
              {fieldErrors.city ? (
                <div className="invalid-feedback d-block">{fieldErrors.city}</div>
              ) : null}
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold" htmlFor="signup-facebook-profile">
                Profil Facebook
               
              </label>
              <input
                id="signup-facebook-profile"
                type="url"
                className={`form-control ${fieldErrors.facebookProfile ? 'is-invalid' : ''}`}
                placeholder="https://www.facebook.com/..."
                value={form.facebookProfile}
                onChange={(event) => updateField('facebookProfile', event.target.value)}
              />
              {fieldErrors.facebookProfile ? (
                <div className="invalid-feedback d-block">{fieldErrors.facebookProfile}</div>
              ) : null}
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold" htmlFor="signup-birth-day">
                Data nasterii <span className="text-danger">*</span>
              </label>
              <div className="row g-2">
                <div className="col-4">
                  <select
                    id="signup-birth-day"
                    className={`form-select ${fieldErrors.birthDate ? 'is-invalid' : ''}`}
                    value={form.birthDay}
                    onChange={(event) => updateField('birthDay', event.target.value)}
                  >
                    <option value="">Zi</option>
                    {DAY_OPTIONS.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-4">
                  <select
                    className={`form-select ${fieldErrors.birthDate ? 'is-invalid' : ''}`}
                    value={form.birthMonth}
                    onChange={(event) => updateField('birthMonth', event.target.value)}
                  >
                    <option value="">Luna</option>
                    {MONTH_OPTIONS.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-4">
                  <select
                    className={`form-select ${fieldErrors.birthDate ? 'is-invalid' : ''}`}
                    value={form.birthYear}
                    onChange={(event) => updateField('birthYear', event.target.value)}
                  >
                    <option value="">An</option>
                    {YEAR_OPTIONS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-muted small mt-2 mb-0">
                Selecteaza separat ziua, luna si anul nasterii.
              </p>
              {fieldErrors.birthDate ? (
                <div className="invalid-feedback d-block">{fieldErrors.birthDate}</div>
              ) : null}
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold" htmlFor="signup-images">
                Ataseaza imagini <span className="text-danger">*</span>
              </label>
              <input
                id="signup-images"
                type="file"
                accept="image/*"
                multiple
                className={`form-control ${fieldErrors.images ? 'is-invalid' : ''}`}
                onChange={handleFileChange}
              />
              <p className="text-muted small mt-2 mb-0">
                Poti selecta oricate imagini doresti. 
              </p>
              {selectedFiles.length > 0 ? (
                <div className="mt-3">
                  {selectedFiles.map((file) => (
                    <span key={`${file.name}-${file.lastModified}`} className="badge text-bg-light border me-2 mb-2">
                      {file.name}
                    </span>
                  ))}
                </div>
              ) : null}
              {fieldErrors.images ? (
                <div className="invalid-feedback d-block">{fieldErrors.images}</div>
              ) : null}
            </div>

            <div className="col-12">
              <div className={`form-check ${fieldErrors.privacyConsent ? 'is-invalid' : ''}`}>
                <input
                  id="signup-privacy-consent"
                  type="checkbox"
                  className={`form-check-input ${fieldErrors.privacyConsent ? 'is-invalid' : ''}`}
                  checked={form.privacyConsent}
                  onChange={(event) => updateField('privacyConsent', event.target.checked)}
                />
                <label className="form-check-label text-muted" htmlFor="signup-privacy-consent">
                  Sunt de acord cu prelucrarea datelor personale furnizate prin acest formular pentru analizarea inscrierii. <span className="text-danger">*</span>
                </label>
              </div>
              {fieldErrors.privacyConsent ? (
                <div className="invalid-feedback d-block">{fieldErrors.privacyConsent}</div>
              ) : null}
            </div>
          </div>

          <div className="mt-4 pt-4 border-top">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-4">
              <div>
                <h6 className="mb-1">Ultimul pas</h6>
                <p className="text-muted mb-0">
                  Dupa trimitere, echipa Luna Di Miele va analiza inscrierea si te va contacta pentru urmatorii pasi.
                </p>
              </div>

              <button
                type="submit"
                className="btn btn-primary px-4 py-3 rounded-3 fw-semibold"
                style={{ minWidth: '220px' }}
                disabled={isSaving}
              >
                {isSaving ? 'Se trimite...' : 'Trimite inscrierea'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
